import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = authenticated user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_err) {
      // 401 = no active session; other errors also fall through to anonymous.
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, pseudonym) => {
    const { data } = await api.post("/auth/register", { email, password, pseudonym });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_err) {
      // Network / already-expired session — safe to clear locally.
    }
    setUser(false);
  }, []);

  const updatePseudonym = useCallback(async (pseudonym) => {
    const { data } = await api.patch("/auth/pseudonym", { pseudonym });
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      register,
      logout,
      updatePseudonym,
      formatApiError,
      refresh: fetchMe,
    }),
    [user, login, register, logout, updatePseudonym, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
